import { utcToZonedTime } from "date-fns-tz";
import { inject } from "mobx-react";
import { Fragment, useMemo } from "react";
import { FeedResult_SitesFeedPubsubResult_Fragment, FeedResultFragment } from "@ihr-radioedit/inferno-webapi";
import {
  getEventEndDate,
  getEventStartDate,
  getEventVenueAddress,
  getEventVenueName,
} from "../../../../core/lib/calendar";
import { isCalendarFeedBlock } from "../../../../core/lib/guards";
import { getDateFormat } from "@ihr-radioedit/inferno-core";
import {
  reverseRoute,
  DATETIME_DISPLAY_FORMATS,
  zonedTimeFormat,
  isCalendarPublishRecord,
  isFeedResult,
} from "@inferno/renderer-shared-core";
import { Heading } from "../../../../core/ui";
import Grid from "../../../../core/ui/Grid.component";
import { CoastFeedArticlesProps } from "./CoastFeedArticles.component";
import CoastFeedCalendarItem from "./CoastFeedCalendarItem.component";
import { CoastFeedNoResults } from "./CoastFeedNoResults.component";
import { getDisplayHints } from "../../../../core/services/Sites.utils";
import styles from "./CoastFeedCalendar.module.scss";

export const CoastFeedCalendar = inject("store")(({ store, content, block, title }: CoastFeedArticlesProps) => {
  if (!store) {
    return null;
  }

  const { site } = store;

  if (!isCalendarFeedBlock(block)) {
    return null;
  }

  const header = useMemo(() => (title ? <Heading>{title}</Heading> : null), [title]);

  const items =
    content?.map((item: FeedResultFragment, i) => {
      if (!isFeedResult(item) || !isCalendarPublishRecord(item.record)) {
        return null;
      }
      const { record } = item;
      const { fields } = record?.payload;

      let monthHeader = null;
      const prevItem = { ...(content[i - 1] ?? {}) } as FeedResult_SitesFeedPubsubResult_Fragment;
      const timezone = site.index?.timeZone || "UTC";
      const utcTime = getEventStartDate(prevItem?.record?.payload?.fields) ?? new Date().getUTCDate();
      const prevStartDate = utcToZonedTime(utcTime, timezone);
      const currentStartDate = utcToZonedTime(getEventStartDate(fields) ?? new Date().getUTCDate(), timezone);

      if (!i || prevStartDate.getMonth() !== currentStartDate.getMonth()) {
        const outputFormat = DATETIME_DISPLAY_FORMATS.FULL_MONTH_YEAR;
        const monthText = zonedTimeFormat({ date: currentStartDate, timezone, outputFormat });
        monthHeader = (
          <h2 className={styles.monthTitle}>
            <time>{monthText}</time>
          </h2>
        );
      }

      const displayHints = getDisplayHints(block.tags || []);
      const { showDateStamp } = displayHints;

      return (
        <div className={styles.coastCalendarItem} key={i}>
          {monthHeader}
          <CoastFeedCalendarItem
            title={record.summary.title}
            url={reverseRoute(site, "calendar_content", { slug: record.slug }) || ""}
            slug={`item_${i}`}
            action="item_selected"
            thumb={record.summary.image ?? ""}
            startDateTime={getEventStartDate(fields) ?? record.pub_start}
            endDateTime={getEventEndDate(fields) ?? record.pub_start}
            dateFormat={getDateFormat(site)}
            venueName={getEventVenueName(fields) ?? ""}
            venueAddress={getEventVenueAddress(fields) ?? ""}
            showDateStamp={showDateStamp}
          />
        </div>
      );
    }) ?? [];

  const isNoEvents = items.every(item => item === null);

  return (
    <Fragment>
      {header}
      <Grid columns={1}>{!isNoEvents ? items : <CoastFeedNoResults />}</Grid>
    </Fragment>
  );
});

export default CoastFeedCalendar;
